// Hook para gerenciar o estado do orçamento do fornecedor

import { useState, useEffect, useCallback } from "react";
import { telefonePrincipal } from "../utils/orcamento-formatters";
import {
  OrcamentoFornecedorData,
  DadosIdentificacao,
  DadosValores,
  DadosAssinatura,
  DadosAceite,
  ResponsavelOrcamento,
  ValidationResult,
} from "../types/orcamento-fornecedor.types";
import {
  validarEtapa1,
  validarEtapa2,
  validarEtapa3,
  validarEtapa4,
} from "../utils/orcamento-validations";
import { orcamentoFornecedorService } from "../services/orcamentoFornecedorService";
import { CLAUSULA_CANCELAMENTO_PADRAO } from "../utils/orcamentoDefaults";

interface UseOrcamentoFornecedorReturn {
  // Estado
  dadosOrcamento: OrcamentoFornecedorData | null;
  etapaAtual: number;
  loading: boolean;
  error: string | null;
  saving: boolean; // Novo: indica se está salvando

  // Navegação
  irParaEtapa: (etapa: number) => void;
  proximaEtapa: () => Promise<void>; // Agora é async
  etapaAnterior: () => void;

  // Dados das etapas
  dadosEtapa1: Partial<DadosIdentificacao>;
  dadosEtapa2: Partial<DadosValores>;
  dadosEtapa3: Partial<DadosAssinatura>;
  dadosEtapa4: Partial<DadosAceite>;

  // Atualização de dados
  atualizarDadosEtapa1: (dados: Partial<DadosIdentificacao>) => void;
  atualizarDadosEtapa2: (dados: Partial<DadosValores>) => void;
  atualizarDadosEtapa3: (dados: Partial<DadosAssinatura>) => void;
  atualizarDadosEtapa4: (dados: Partial<DadosAceite>) => void;

  // Validações
  validarEtapaAtual: () => ValidationResult;

  // Ações
  salvarEtapaAtual: () => Promise<void>; // Novo: salva a etapa atual
  finalizarOrcamento: (htmlContent?: string) => Promise<void>;
}

export const useOrcamentoFornecedor = (
  token: string,
): UseOrcamentoFornecedorReturn => {
  const [dadosOrcamento, setDadosOrcamento] =
    useState<OrcamentoFornecedorData | null>(null);
  const [etapaAtual, setEtapaAtual] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Estados das etapas
  const [dadosEtapa1, setDadosEtapa1] = useState<Partial<DadosIdentificacao>>(
    {},
  );
  const [dadosEtapa2, setDadosEtapa2] = useState<Partial<DadosValores>>({
    itens: {},
  });
  const [dadosEtapa3, setDadosEtapa3] = useState<Partial<DadosAssinatura>>({
    tipo: "manual",
  });
  const [dadosEtapa4, setDadosEtapa4] = useState<Partial<DadosAceite>>({
    termo1: false,
    termo2: false,
  });

  // Carregar dados do orçamento
  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setError(null);

        const resposta = await orcamentoFornecedorService.carregarOrcamento(token);

        // A API anterior devolvia um CNAE só; vira lista de um. Normalizado
        // aqui, na entrada, para o resto da tela só conhecer a lista.
        const identificacao = resposta.dadosIdentificacao
          ? (() => {
              const { cnaeId, cnaeIds, ...resto } = resposta.dadosIdentificacao;
              return { ...resto, cnaeIds: cnaeIds ?? (cnaeId ? [cnaeId] : []) };
            })()
          : undefined;
        const data = { ...resposta, dadosIdentificacao: identificacao };

        setDadosOrcamento(data);

        // Se etapaAtual for 0, começar na etapa 1. Na edição interna a cotação
        // costuma estar concluída (etapa 4), e o que se veio corrigir são os
        // valores: abre na etapa 2 quando ela já foi preenchida.
        const etapaInicial = data.edicaoInterna
          ? Math.min(Math.max(data.etapaAtual, 1), 2)
          : data.etapaAtual === 0
            ? 1
            : data.etapaAtual;
        setEtapaAtual(etapaInicial);

        // Restaurar dados salvos de cada etapa (se existirem - vem como null se não preenchidos)
        if (data.dadosIdentificacao) {
          setDadosEtapa1(data.dadosIdentificacao);
        }

        if (data.dadosValores) {
          setDadosEtapa2(data.dadosValores);
        } else {
          // Pré-preencher data com data atual e inicializar itens vazios
          const itensVazios: Record<string, number> = {};
          if (data.itens && Array.isArray(data.itens)) {
            data.itens.forEach((item) => {
              itensVazios[item.id] = item.valorUnitario || 0;
            });
          }

          setDadosEtapa2((prev) => ({
            ...prev,
            dataOrcamento: new Date().toISOString().split("T")[0],
            itens: itensVazios,
            // Só aqui, no ramo em que a etapa 2 nunca foi salva. Pré-preencher
            // também quando `dadosValores` existe faria a cláusula voltar para
            // quem a apagou de propósito.
            clausulaCancelamento: CLAUSULA_CANCELAMENTO_PADRAO,
          }));
        }

        if (data.dadosAssinatura) {
          setDadosEtapa3(data.dadosAssinatura);
        }

        setLoading(false);
      } catch (err: any) {
        const errorMessage =
          err.message ||
          "Erro ao carregar orçamento. Verifique se o link é válido.";
        setError(errorMessage);
        setLoading(false);
      }
    };

    carregarDados();
  }, [token]);

  // Atualização de dados
  const atualizarDadosEtapa1 = useCallback(
    (dados: Partial<DadosIdentificacao>) => {
      setDadosEtapa1((prev) => ({ ...prev, ...dados }));
    },
    [],
  );

  const atualizarDadosEtapa2 = useCallback((dados: Partial<DadosValores>) => {
    setDadosEtapa2((prev) => ({ ...prev, ...dados }));
  }, []);

  const atualizarDadosEtapa3 = useCallback(
    (dados: Partial<DadosAssinatura>) => {
      setDadosEtapa3((prev) => ({ ...prev, ...dados }));
    },
    [],
  );

  const atualizarDadosEtapa4 = useCallback((dados: Partial<DadosAceite>) => {
    setDadosEtapa4((prev) => ({ ...prev, ...dados }));
  }, []);

  // Validação
  const validarEtapaAtual = useCallback((): ValidationResult => {
    switch (etapaAtual) {
      case 1:
        return validarEtapa1(
          dadosEtapa1.cnaeIds ?? [],
          dadosEtapa1.responsavel || ({} as ResponsavelOrcamento),
          dadosEtapa1.telefoneEmpresa ??
            telefonePrincipal(dadosOrcamento?.fornecedor?.telefone),
        );
      case 2:
        return validarEtapa2(dadosEtapa2);
      case 3:
        return validarEtapa3(dadosEtapa3);
      case 4:
        return validarEtapa4(dadosEtapa4);
      default:
        return { valid: true, errors: {} };
    }
    // dadosOrcamento entra porque o telefone cai no do cadastro quando o
    // fornecedor não mexe no campo. Sem ele aqui, a validação usaria a
    // versão nula de antes do carregamento e reprovaria sem motivo.
  }, [etapaAtual, dadosOrcamento, dadosEtapa1, dadosEtapa2, dadosEtapa3, dadosEtapa4]);

  // Salvar etapa atual na API
  const salvarEtapaAtual = useCallback(async () => {
    try {
      setSaving(true);

      switch (etapaAtual) {
        case 1:
          if (dadosEtapa1.cnaeIds?.length && dadosEtapa1.responsavel) {
            await orcamentoFornecedorService.salvarEtapa1(token, {
              cnaeId: dadosEtapa1.cnaeIds[0],
              cnaeIds: dadosEtapa1.cnaeIds,
              responsavel: dadosEtapa1.responsavel,
              // Quem não mexeu no campo está confirmando o do cadastro.
              telefoneEmpresa:
                dadosEtapa1.telefoneEmpresa ??
            telefonePrincipal(dadosOrcamento?.fornecedor?.telefone),
            });
          }
          break;

        case 2:
          if (
            dadosEtapa2.dataOrcamento &&
            dadosEtapa2.validadeDias &&
            dadosEtapa2.itens
          ) {
            const response = await orcamentoFornecedorService.salvarEtapa2(
              token,
              {
                dataOrcamento: dadosEtapa2.dataOrcamento,
                validadeDias: dadosEtapa2.validadeDias,
                itens: dadosEtapa2.itens,
                formaPagamento: dadosEtapa2.formaPagamento || "",
                clausulaCancelamento: dadosEtapa2.clausulaCancelamento || "",
                observacoesProposta: dadosEtapa2.observacoesProposta || "",
              },
            );

            // Atualizar total calculado pela API
            if (response.data?.totalGeral) {
              setDadosEtapa2((prev) => ({
                ...prev,
                totalGeral: response.data!.totalGeral,
              }));
            }
          }
          break;

        case 3:
          if (dadosEtapa3.tipo && dadosEtapa3.assinaturaData) {
            // Verificar se a assinatura já está salva no backend (URL externa)
            const isExternalUrl =
              dadosEtapa3.assinaturaData.startsWith("http://") ||
              dadosEtapa3.assinaturaData.startsWith("https://");

            // Só enviar se for uma nova assinatura (data URL)
            if (!isExternalUrl) {
              await orcamentoFornecedorService.salvarEtapa3(token, {
                tipo: dadosEtapa3.tipo,
                assinaturaData: dadosEtapa3.assinaturaData,
              });
            }
            // Se já é URL externa, pular o envio (já está salva)
          }
          break;

        default:
          break;
      }

      setSaving(false);
    } catch (err: any) {
      setSaving(false);
      const errorMessage =
        err.message || "Erro ao salvar dados. Tente novamente.";
      throw new Error(errorMessage);
    }
  }, [token, etapaAtual, dadosOrcamento, dadosEtapa1, dadosEtapa2, dadosEtapa3]);

  // Navegação
  const irParaEtapa = useCallback((etapa: number) => {
    if (etapa >= 1 && etapa <= 4) {
      setEtapaAtual(etapa);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const proximaEtapa = useCallback(async () => {
    if (etapaAtual < 4) {
      // Salvar a etapa atual antes de avançar
      await salvarEtapaAtual();
      setEtapaAtual((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [etapaAtual, salvarEtapaAtual]);

  const etapaAnterior = useCallback(() => {
    if (etapaAtual > 1) {
      setEtapaAtual((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [etapaAtual]);

  // Finalizar orçamento
  const finalizarOrcamento = useCallback(
    async (pdfBase64?: string) => {
      try {
        setSaving(true);

        await orcamentoFornecedorService.finalizarOrcamento(token, {
          termo1: dadosEtapa4.termo1 || false,
        });

        // Se HTML foi fornecido, enviar para gerar PDF no backend
        if (pdfBase64) {
          try {
            await orcamentoFornecedorService.gerarPdfFromHtml(token, pdfBase64);
          } catch (pdfError) {
            console.error("Erro ao gerar PDF, mas continuando:", pdfError);
            // Não bloquear a finalização se PDF falhar
          }
        }

        setSaving(false);
      } catch (err: any) {
        setSaving(false);
        const errorMessage =
          err.message || "Erro ao finalizar orçamento. Tente novamente.";
        throw new Error(errorMessage);
      }
    },
    [token, dadosEtapa4],
  );

  return {
    dadosOrcamento,
    etapaAtual,
    loading,
    saving,
    error,
    irParaEtapa,
    proximaEtapa,
    etapaAnterior,
    dadosEtapa1,
    dadosEtapa2,
    dadosEtapa3,
    dadosEtapa4,
    atualizarDadosEtapa1,
    atualizarDadosEtapa2,
    atualizarDadosEtapa3,
    atualizarDadosEtapa4,
    validarEtapaAtual,
    salvarEtapaAtual,
    finalizarOrcamento,
  };
};
